import { aircraftTypeQuery } from "@common/api/aircraft-type"
import type { TypeCertificateModelVariantGroup } from "@common/client"
import { useQuery } from "@tanstack/react-query"
import { useMemo } from "react"
import type { FieldValues, Path } from "react-hook-form"
import { type OptionGroup, SelectField } from "../fields/select"

type Props<T extends FieldValues> = {
  name: Path<T>
  label?: string
  className?: string
  disabled?: boolean
  optional?: boolean
  clearable?: boolean
}

export function variantLabel(variant: {
  marketing_name: string
  icao_type_designator: string | null
}): string {
  return variant.icao_type_designator
    ? `${variant.marketing_name} (${variant.icao_type_designator})`
    : variant.marketing_name
}

export function aircraftTypeGroups(
  groups: ReadonlyArray<TypeCertificateModelVariantGroup>,
): ReadonlyArray<OptionGroup<string>> {
  return groups.map((group) => ({
    label: `${group.current_holder_name} ${group.model_designation}`,
    options: group.variants.map((variant) => ({
      value: variant.id,
      label: variantLabel(variant),
    })),
  }))
}

export function AircraftTypeField<T extends FieldValues>({
  name,
  label = "Aircraft type",
  className,
  disabled,
  optional,
  clearable,
}: Props<T>) {
  const { data, isLoading } = useQuery(aircraftTypeQuery(undefined))

  const groups = useMemo(() => aircraftTypeGroups(data ?? []), [data])

  return (
    <SelectField<T>
      name={name}
      label={label}
      className={className}
      disabled={disabled || isLoading}
      placeholder={isLoading ? "Loading…" : "Select an aircraft type"}
      groups={groups}
      optional={optional}
      clearable={clearable}
    />
  )
}
